import { Link, useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { NavItem } from '../../types/navigation';
import type { AdminNavItem } from '../../constants/adminNav';
import { tokenManager } from '../../api/client';

interface SidebarPanelProps {
  title: string;
  items: (NavItem | AdminNavItem)[];
  onNavigate?: () => void;
}

function isExternalAdminLink(item: NavItem | AdminNavItem): item is AdminNavItem & { external: true } {
  return 'external' in item && (item as AdminNavItem).external === true;
}

export function SidebarPanel({ title, items, onNavigate }: SidebarPanelProps) {
  const location = useLocation();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="py-2">
      <h3 className="px-4 mb-1 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
        {title}
      </h3>
      {items.map(item => {
        if (isExternalAdminLink(item)) {
          return (
            <button
              key={item.key}
              onClick={() => {
                const jwt = tokenManager.getToken();
                if (jwt) window.open(`${item.path}?token=${jwt}`, '_blank');
              }}
              data-testid={`sidebar-item-${item.key}`}
              className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100 w-full text-left"
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              <ExternalLink className="h-3 w-3 text-gray-400 dark:text-slate-500 shrink-0" />
            </button>
          );
        }

        return (
          <Link
            key={item.key}
            to={item.path}
            onClick={onNavigate}
            data-testid={`sidebar-item-${item.key}`}
            className={cn(
              'flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors',
              isActive(item.path)
                ? 'bg-gray-50 text-gray-900 border-r-2 border-gray-900 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-100'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
            )}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{item.label}</span>
            {'badge' in item && (item as NavItem).badge === 'preview' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300">
                Preview
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
